import { useLayoutEffect } from "react";
import { Spinner } from "@/components/ui/spinner";

/*
 * First load: index.html paints the splash (#app-splash, a blueprint sheet with
 * the Uplodio mark drawing itself) before any JavaScript runs, and it covers
 * the whole app's download (every page is in one bundle). It stays up while
 * anything renders <AppLoading /> — sign-in state, the onboarding, mailbox and
 * plan checks — or holds it with useSplashHold (the dashboard's first data),
 * and fades out once nothing does: the app appears whole, not through a chain
 * of different loaders, wherever the mark's loop is. After that, <AppLoading />
 * is a centred spinner.
 */

/** Never hold the splash longer than this, whatever is still loading. */
const SPLASH_MAX_MS = 15_000;

let holds = 0;
let revealed = false;
let routerBusy = () => false;

function reveal() {
  if (revealed) return;
  revealed = true;
  const splash = document.getElementById("app-splash");
  if (!splash) return;
  splash.dataset.state = "out";
  setTimeout(() => splash.remove(), 250);
}

/** Reveal once nothing holds the splash; two frames let the page commit and paint first. */
function scheduleReveal() {
  if (revealed) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (holds === 0 && !routerBusy()) reveal();
    }),
  );
}

/**
 * Wires the splash to the router: a page still loading holds it like an
 * <AppLoading />. Call once, before rendering.
 */
export function watchSplash(router: {
  state: { initialized: boolean; navigation: { state: string } };
  subscribe: (listener: () => void) => () => void;
}) {
  routerBusy = () => !router.state.initialized || router.state.navigation.state !== "idle";
  router.subscribe(scheduleReveal);
  setTimeout(reveal, SPLASH_MAX_MS);
}

/**
 * Keeps the first-load splash up while `active`, with the page rendering
 * underneath it (the dashboard fills in its first data unseen). No effect
 * once the splash is gone.
 */
export function useSplashHold(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    holds += 1;
    return () => {
      holds -= 1;
      scheduleReveal();
    };
  }, [active]);
}

/** What a "not ready yet" state renders: holds the first-load splash, a spinner after. */
export function AppLoading() {
  useLayoutEffect(() => {
    holds += 1;
    return () => {
      holds -= 1;
      scheduleReveal();
    };
  }, []);

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-background p-4">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}
